/** 全局常量：接口前缀、分页、TTL、限流规则。前后端共用，避免魔法数字。 */

export const APP_NAME = '大学英语三辅助学习平台';
export const APP_SLUG = 'en3-platform';
export const API_PREFIX = '/api';

/** 版本号在 Worker 侧可用 env.APP_VERSION 覆盖，前端只作兜底展示。 */
export const FALLBACK_APP_VERSION = '0.1.0';

export const PAGINATION = {
  defaultPageSize: 20,
  maxPageSize: 100,
} as const;

/** 缓存 TTL（秒）。KV 的 expirationTtl 与该表一一对应，便于统一调整。 */
export const CACHE_TTL = {
  /** 响应类缓存：教材/单元/文章读接口 */
  responseSeconds: 5 * 60,
  /** 词典词条 */
  dictionarySeconds: 30 * 24 * 60 * 60,
  /** 词典未命中负缓存，防止打穿到上游 */
  dictionaryNegativeSeconds: 60 * 60,
  /** 长难句分析结果 */
  sentenceAnalysisSeconds: 30 * 24 * 60 * 60,
  /** 学习建议 */
  adviceSeconds: 7 * 24 * 60 * 60,
} as const;

/** 缓存键前缀，集中管理避免各处拼接字符串。 */
export const CACHE_KEYS = {
  dictionary: (lemma: string) => `dict:v1:${lemma}`,
  dictionaryNegative: (lemma: string) => `dictmiss:v1:${lemma}`,
  sentenceAnalysis: (hash: string) => `sent:v1:${hash}`,
  advice: (articleId: string, cefr: string, mode: string) => `adv:v1:${articleId}:${cefr}:${mode}`,
  response: (path: string) => `res:v1:${path}`,
} as const;

/** 认证相关。access token 短，refresh token 长且落 D1 可撤销。 */
export const AUTH = {
  accessTokenTtlSeconds: 15 * 60,
  refreshTokenTtlSeconds: 30 * 24 * 60 * 60,
  refreshCookieName: 'en3_refresh',
  passwordMinLength: 8,
  passwordMaxLength: 128,
  /**
   * WebCrypto PBKDF2-SHA256 迭代次数（Workers 无 bcrypt/scrypt）。
   *
   * ⚠️ `pbkdf2IterationsMax` 是 **Cloudflare Workers 运行时的硬上限**，不是"建议值"：
   * 超过它的 `deriveBits` 会直接抛
   * `NotSupportedError: Pbkdf2 failed: iteration counts above 100000 are not supported`。
   * 这不会让请求"慢一点"，而是让注册/登录 100% 变成 500 —— 线上出过一次：
   * 默认值曾写成 210,000，于是所有注册都失败，前端只看到一句笼统的错误。
   * 因此默认值就取运行时上限，并且任何来源的配置都会被 `resolveIterations()` 钳制回区间内。
   */
  pbkdf2Iterations: 100_000,
  /** 迭代次数下限：低于它视为无效配置（防止误配成 1，让哈希形同虚设） */
  pbkdf2IterationsMin: 1_000,
  /** 迭代次数上限 = Workers 运行时硬上限；超过即抛异常，必须钳制 */
  pbkdf2IterationsMax: 100_000,
} as const;

/** 限流规则表。作用域 → 窗口内允许次数 / 窗口秒数。 */
export const RATE_LIMITS = {
  auth: { limit: 10, windowSeconds: 10 * 60 },
  dictionaryAnonymous: { limit: 300, windowSeconds: 60 },
  dictionaryUser: { limit: 120, windowSeconds: 60 },
  sentenceAnalyze: { limit: 20, windowSeconds: 60 },
  levelAssess: { limit: 5, windowSeconds: 60 },
  /**
   * 导入：按天限流。
   *
   * 取 60/天 而不是更小的值：一位教师建一门课，一个学期要导入的可能就是
   * 「一本教材 × 10 单元 × 2–3 篇」这个量级，卡在 20 会直接把正常使用拦住；
   * 而 60 篇/天已经远超人工粘贴的速度，仍然能挡住脚本刷库。
   */
  import: { limit: 60, windowSeconds: 24 * 60 * 60 },
  /**
   * 复习提交：按天限流。
   *
   * 复习是"一天反复来几次"的行为，如果按分钟限流，用户下午复习完 200 张卡
   * 晚上再来就被挡住了。600 张/天 ≈ 每天 10 轮 × 60 张，足够真实使用，
   * 又能挡住脚本刷复习日志。
   */
  review: { limit: 600, windowSeconds: 24 * 60 * 60 },
  /** 导出：重操作（要拼整份 CSV），按小时限流 */
  export: { limit: 10, windowSeconds: 60 * 60 },
  /** 埋点上报：批量接口，给足余量 */
  events: { limit: 240, windowSeconds: 60 },
  global: { limit: 300, windowSeconds: 60 },
} as const;

export type RateLimitScope = keyof typeof RATE_LIMITS;

/** 导入限制，与 413 / 422 错误码配合。 */
export const IMPORT_LIMITS = {
  maxArticleChars: 120_000,
  maxTitleLength: 200,
  /** 超过该字数时正文落 R2，D1 只存元数据 */
  r2ThresholdChars: 60_000,
} as const;
